import React, { useMemo } from 'react';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { Box, Divider, IconButton, Tooltip } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { DomainSwitcher } from '../navigation/DomainSwitcher';
import { SmartSecondarySidebar } from '../navigation/SmartSecondarySidebar';
import { useSidebarState } from '../../hooks/useSidebarState';
import { useVRPToolpadNavigation } from '../../providers/ToolpadAppProvider';
import { withFullContextLoading } from '../common/withContextLoading';

export interface CustomDashboardLayoutProps {
  children: React.ReactNode;
}

/**
 * Custom Dashboard Layout with DECOY Domain Switcher and Smart Secondary Sidebar
 * 
 * Extends MUI Toolpad DashboardLayout to include:
 * - Domain Switcher in sidebar footer
 * - Smart Secondary Sidebar with Toolpad navigation context integration
 * - Enhanced context switching based on navigation state validation
 * - Custom DECOY branding elements
 * - Maintains all Toolpad functionality
 * 
 * Enhanced Features:
 * - Uses useVRPToolpadNavigation for robust navigation state detection
 * - Feature-based secondary sidebar visibility (projects, table-editor, dashboard)
 * - Project context validation for contextual secondary sidebar display
 * - Debug logging for secondary sidebar visibility decisions
 */
export const CustomDashboardLayout: React.FC<CustomDashboardLayoutProps> = ({
  children,
}) => {
  const location = useLocation();
  const { isCollapsed, toggleSidebar, isAutoCollapsed, shouldShow, isMobile, isTablet } = useSidebarState();
  const { navigation } = useVRPToolpadNavigation();

  // Enhanced Smart Context Switching Logic - Uses Toolpad navigation state
  const showSecondarySidebar = useMemo(() => {
    if (!shouldShow) return false;
    
    // Use enhanced navigation context instead of pathname string matching
    const { currentFeature, hasValidProject } = navigation;
    
    console.log('[CustomDashboardLayout] Secondary sidebar visibility check:', {
      currentFeature,
      hasValidProject,
      shouldShow,
      pathname: navigation.pathname
    });
    
    // Feature-based secondary sidebar visibility
    switch (currentFeature) {
      case 'projects':
        // Projects: Always show project hierarchy in secondary sidebar
        return true;
        
      case 'table-editor':
        // Table Editor: Show flat table list across all projects
        return true;
        
      case 'dashboard':
        // Dashboard: Show project overview if we have project context
        return hasValidProject;
        
      case 'settings':
      case 'tools':
        // Settings/Tools: No secondary sidebar needed
        return false;
        
      default:
        // For any route with project context (vehicles, jobs, locations, etc.)
        // Show project hierarchy to maintain navigation context
        if (hasValidProject) {
          return true;
        }
        
        // Unknown features or routes without project context
        return false;
    }
  }, [shouldShow, navigation]);

  // Custom footer component for primary sidebar
  const SidebarFooter = () => (
    <Box sx={{ p: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <DomainSwitcher 
        variant="button" 
        showLabel={true} 
        size="small" 
      />
    </Box>
  );

  // Secondary sidebar toggle component
  const SecondarySidebarToggle = () => (
    <Tooltip title={isCollapsed ? "Expand secondary sidebar" : "Collapse secondary sidebar"} placement="right">
      <IconButton
        onClick={toggleSidebar}
        size="small"
        sx={{
          position: 'absolute',
          right: -16,
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '50%',
          width: 32,
          height: 32,
          zIndex: 1000,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {isCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
      </IconButton>
    </Tooltip>
  );

  // Theme toggle positioned in top-right corner
  const TopRightThemeToggle = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        right: 24,
        zIndex: 1200, // Above most other elements
      }}
    >
      <DarkModeToggle variant="icon" size="medium" />
    </Box>
  );

  // Note: Secondary sidebar is now implemented using proper flex layout
  // instead of manual positioning for better Toolpad compatibility

  return (
    <DashboardLayout
      slots={{
        sidebarFooter: SidebarFooter,
      }}
      sx={{
        display: 'flex',
        width: '100%',
        height: '100vh',
      }}
    >
      <Box sx={{ display: 'flex', flex: 1, height: '100%' }}>
        {/* Secondary sidebar with proper flex positioning */}
        {showSecondarySidebar && (
          <Box
            component="aside"
            sx={{
              width: isCollapsed ? 56 : 280, // Fixed pixel values per Supabase spec
              flexShrink: 0,
              backgroundColor: 'background.paper', // surface_secondary per Supabase spec
              borderRight: '1px solid',
              borderColor: 'divider', // border_muted per Supabase spec
              height: '100%',
              // Use flex positioning instead of fixed to avoid gaps
              position: 'relative',
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              overflow: 'hidden',
              // Auto-collapse behavior: hide on mobile, collapse on tablet, full on desktop
              display: isMobile ? 'none' : 'flex',
            }}
          >
            <SmartSecondarySidebar 
              width={isCollapsed ? 56 : 280} 
              collapsed={isCollapsed}
              onCollapseChange={toggleSidebar}
            />
            {/* Show toggle button unless on mobile */}
            {!isMobile && <SecondarySidebarToggle />}
          </Box>
        )}
        
        {/* Main content area - flex takes remaining space automatically */}
        <Box
          component="main"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            height: '100%',
            backgroundColor: 'background.default',
            // Ensure proper padding for content
            padding: 0,
          }}
        >
          {children}
        </Box>
      </Box>
    </DashboardLayout>
  );
};

// Apply loading boundary for context dependencies
const CustomDashboardLayoutWithLoading = withFullContextLoading(CustomDashboardLayout, {
  loadingMessage: 'Loading dashboard interface...',
  skeletonVariant: 'dashboard',
  fullHeight: true,
  minLoadTime: 500,
});

export default CustomDashboardLayoutWithLoading;